import { useState, useEffect, useRef } from 'react';
import { useConversations, useMessages } from '../hooks/useMessages';
import { useAuth } from '../context/AuthContext';

const COLORS = ['#C94B30','#1D9E75','#534AB7','#BA7517','#085041','#3C3489'];
const getColor = s => COLORS[(s?.charCodeAt(0)||0) % COLORS.length];
const initials = n => (n||'U').split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase();
function timeAgo(ts) {
  if (!ts?.toDate) return '';
  try {
    const s = Math.floor((Date.now() - ts.toDate().getTime()) / 1000);
    if (s < 60) return 'now'; if (s < 3600) return Math.floor(s/60)+'m'; return Math.floor(s/3600)+'h';
  } catch { return ''; }
}

function ChatArea({ conversationId, currentUser, otherName }) {
  const { messages, loading, sendMessage } = useMessages(conversationId);
  const [text, setText] = useState('');
  const bottomRef = useRef(null);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior:'smooth' }); }, [messages]);

  async function handleSend() {
    if (!text.trim()) return;
    const t = text; setText('');
    await sendMessage(t);
  }
  function handleKey(e) { if (e.key==='Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }

  return (
    <>
      <div style={{ padding:'12px 16px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', gap:10, flexShrink:0 }}>
        <div style={{ width:32, height:32, borderRadius:'50%', background:getColor(otherName), display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:600, color:'white' }}>{initials(otherName)}</div>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:14, fontWeight:500, color:'var(--text)' }}>{otherName}</div>
          <div style={{ fontSize:11, color:'#1D9E75' }}>● Online</div>
        </div>
      </div>
      <div style={{ flex:1, overflowY:'auto', padding:16, display:'flex', flexDirection:'column', gap:10 }}>
        {loading && <div style={{ display:'flex', justifyContent:'center', padding:20 }}><div style={{ width:20, height:20, borderRadius:'50%', border:'2px solid var(--border)', borderTopColor:'var(--sunset)', animation:'spin 0.8s linear infinite' }} /></div>}
        {!loading && messages.length === 0 && <div style={{ textAlign:'center', color:'var(--muted)', fontSize:13, marginTop:40 }}>Say hello to {otherName}! 👋</div>}
        {messages.map(msg => {
          const isMe = msg.senderId === currentUser?.uid;
          return (
            <div key={msg.id} style={{ display:'flex', flexDirection:'column', alignItems: isMe ? 'flex-end' : 'flex-start' }}>
              <div style={{ maxWidth:'72%', padding:'9px 14px', borderRadius:16, fontSize:13, lineHeight:1.5, wordBreak:'break-word', background: isMe ? 'linear-gradient(135deg, var(--flame), #a83220)' : 'var(--card)', color: isMe ? 'white' : 'var(--text)', border: isMe ? 'none' : '1px solid var(--border)', borderBottomRightRadius: isMe ? 4 : 16, borderBottomLeftRadius: isMe ? 16 : 4 }}>{msg.text}</div>
              <div style={{ fontSize:10, color:'var(--muted)', marginTop:3 }}>{timeAgo(msg.createdAt)}</div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>
      <div style={{ padding:'12px 16px', borderTop:'1px solid var(--border)', display:'flex', gap:8, alignItems:'flex-end', flexShrink:0 }}>
        <textarea className="chat-input" rows={1} placeholder="Message..." value={text} onChange={e => setText(e.target.value)} onKeyDown={handleKey} style={{ flex:1, padding:'10px 14px', borderRadius:22, background:'var(--card)', border:'1px solid var(--border)', color:'var(--text)', fontSize:13, resize:'none', maxHeight:100, fontFamily:'Space Grotesk,sans-serif' }} />
        <button onClick={handleSend} style={{ width:40, height:40, borderRadius:'50%', background:'linear-gradient(135deg, var(--sunset), var(--flame))', border:'none', color:'white', fontSize:16, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>↗</button>
      </div>
    </>
  );
}

export default function MessagesPage() {
  const { user } = useAuth();
  const { conversations, loading } = useConversations();
  const [activeId, setActiveId] = useState(null);
  const activeConvo = conversations.find(c => c.id === activeId);
  const otherName = activeConvo ? Object.entries(activeConvo.memberNames||{}).find(([uid]) => uid !== user?.uid)?.[1] || 'Chat' : '';

  return (
    <div style={{ display:'flex', flexDirection:'column', flex:1, height:'100%', overflow:'hidden' }}>
      <div style={{ padding:'16px 20px 12px', borderBottom:'1px solid var(--border)', background:'var(--earth)', flexShrink:0 }}>
        <h2 style={{ fontFamily:'Sora,sans-serif', fontSize:18, fontWeight:600, color:'var(--text)' }}>Messages</h2>
        <p style={{ fontSize:12, color:'var(--muted)', marginTop:2 }}>Real-time conversations</p>
      </div>
      <div style={{ display:'flex', flex:1, overflow:'hidden' }}>
        <div style={{ width:220, borderRight:'1px solid var(--border)', display:'flex', flexDirection:'column', flexShrink:0, overflow:'hidden' }}>
          <div style={{ padding:'10px 12px', borderBottom:'1px solid var(--border)', flexShrink:0 }}>
            <input type="text" placeholder="Search..." style={{ width:'100%', padding:'7px 10px', borderRadius:20, fontSize:12 }} />
          </div>
          <div style={{ flex:1, overflowY:'auto' }}>
            {loading && <div style={{ display:'flex', justifyContent:'center', padding:20 }}><div style={{ width:16, height:16, borderRadius:'50%', border:'2px solid var(--border)', borderTopColor:'var(--sunset)', animation:'spin 0.8s linear infinite' }} /></div>}
            {!loading && conversations.length === 0 && <div style={{ padding:16, textAlign:'center', fontSize:12, color:'var(--muted)' }}>No conversations yet.<br/>Go to Discover to message someone.</div>}
            {conversations.map(c => {
              const name = Object.entries(c.memberNames||{}).find(([uid]) => uid !== user?.uid)?.[1] || 'Chat';
              return (
                <div key={c.id} onClick={() => setActiveId(c.id)} style={{ display:'flex', alignItems:'center', gap:10, padding:'12px 14px', cursor:'pointer', background: activeId===c.id ? 'rgba(232,168,56,0.08)' : 'transparent', borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
                  <div style={{ width:38, height:38, borderRadius:'50%', background:getColor(name), display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, fontWeight:600, color:'white', flexShrink:0 }}>{initials(name)}</div>
                  <div style={{ flex:1, overflow:'hidden' }}>
                    <div style={{ fontSize:13, fontWeight:500, color:'var(--text)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{name}</div>
                    <div style={{ fontSize:11, color:'var(--muted)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', marginTop:2 }}>{c.lastMessage || 'Start chatting'}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden' }}>
          {activeId && user
            ? <ChatArea conversationId={activeId} currentUser={user} otherName={otherName} />
            : <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', flex:1, color:'var(--muted)', gap:10 }}>
                <div style={{ fontSize:36 }}>💬</div>
                <div style={{ fontSize:14 }}>Select a conversation</div>
                <div style={{ fontSize:12 }}>or discover someone new to chat with</div>
              </div>
          }
        </div>
      </div>
    </div>
  );
}
